import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  ToggleButton,
} from "@mui/material";
import { StyledToggleButtonGroup } from "components/StyledToggleButtonGroup";
import { FC, useState, useEffect, useMemo } from "react";
import CheckIcon from "@mui/icons-material/Check";
import { StyledContainer } from "components/StyledContainer";
import CloseIcon from "@mui/icons-material/Close";
import { DeviceTestStatusEnum } from "../jobDetails/visit/inspection/DeviceTestStatusEnum";
import { format } from "date-fns";
import { FormattedMessage } from "react-intl";
import { ChecklistType, SpecificationType } from "operations/schema/schema";
import cloneDeep from "lodash/cloneDeep";
import { useAppDispatch, useAppSelector } from "store";
import { selectSelectedJobVisit, updateChecklistSpec, updateInspection } from "store/slices/jobs.store";
import PrimaryButton from "components/PrimaryButton";
import IconButton from "@mui/material/IconButton";
import { ChecklistRenderer } from "../jobDetails/visit/ChecklistRenderer";

interface FloorplanDevice extends SpecificationType {
  checklists?: ChecklistType[] | null;
}

interface FloorplanDeviceStatusDialogProps {
  open: boolean;
  onClose: () => void;
  device: FloorplanDevice;
  onSave: (updatedDevice: any) => void;
}

export const FloorplanDeviceStatusDialog: FC<FloorplanDeviceStatusDialogProps> = ({
  open,
  onClose,
  device,
  onSave,
}) => {
  const iconStyle = { marginLeft: 1 };
  const dispatch = useAppDispatch();
  const { inspections } = useAppSelector(selectSelectedJobVisit);

  const inspection = useMemo(
    () => inspections.find((i) => i.specification?.id === device?.id),
    [inspections, device]
  );

  const [checklistStateSpec, setChecklistStateSpec] = useState<ChecklistType | null>(null);
  const [localTestResult, setLocalTestResult] = useState<DeviceTestStatusEnum | null>(null);

  const checklists = inspection?.specification.checklists;

  const serviceDate = device?.lastServiceDate
    ? format(new Date(device.lastServiceDate), "dd/MM/yyyy")
    : "N/A";

  const resultColor =
    device?.resultOfLastService?.toLowerCase() === "pass"
      ? "green"
      : device?.resultOfLastService?.toLowerCase() === "fail"
      ? "red"
      : "grey";

  useEffect(() => {
    if (open && checklistStateSpec === null) { 
      const updatedDevice = inspections.find((i) => i.specification?.id === device?.id);
      const checklist = updatedDevice?.specification?.checklists?.[0];

      if (checklist) {
        setChecklistStateSpec(cloneDeep(checklist));
      } else {
        setChecklistStateSpec(null);
      }
    }
  }, [device, inspections, open, checklistStateSpec]);

  useEffect(() => {
    if (open && localTestResult === null && inspection?.testResult) {
      const testResultEnum = Object.values(DeviceTestStatusEnum).includes(inspection.testResult as DeviceTestStatusEnum)
        ? (inspection.testResult as DeviceTestStatusEnum)
        : null;
      setLocalTestResult(testResultEnum);
    }
  }, [open, inspection, localTestResult]);

  const save = () => {
    if (!inspection) return;

    if (checklistStateSpec) {
      dispatch(updateChecklistSpec({ checklist: checklistStateSpec }));

      const updatedInspectionWithChecklist = {
        ...inspection,
        specification: {
          ...inspection.specification,
          checklists: [checklistStateSpec],
        },
      };

      dispatch(updateInspection({ inspection: updatedInspectionWithChecklist }));
    } else {
      dispatch(updateInspection({ inspection }));
    }

    onSave?.(device);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 10,
          backgroundColor: "rgba(255,255,255,0.8)",
          "&:hover": {
            backgroundColor: "rgba(255,255,255,1)",
          },
        }}
        aria-label="close"
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle>
        {device?.description} ({device?.partNumber})
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{ px: 2 }}>
            <Typography
              noWrap
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                width: '100%',
                display: 'block',
              }}
            >
              {device?.location}
            </Typography>
            <Typography>{device?.subLocation}</Typography>
            <Typography>
              <strong>Last Service:</strong>{" "}
              <span style={{ fontWeight: "bold", color: resultColor }}>
                {device?.resultOfLastService}
              </span>{" "}
              on {serviceDate}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <StyledToggleButtonGroup
              value={inspection?.testResult}
              onChange={(_, value) => {
                if (inspection) {
                  dispatch(updateInspection({ inspection: { ...inspection, testResult: value } }));
                  setLocalTestResult(value);
                }
              }}
              exclusive
              fullWidth
              size="small"
              data-testid="InspectionTestResultToggle"
            >
              <ToggleButton value={DeviceTestStatusEnum.Pass} color="success">
                <FormattedMessage id="inspection.pass" />
                <CheckIcon sx={iconStyle} />
              </ToggleButton>
              <ToggleButton value={DeviceTestStatusEnum.Fail} color="error">
                <FormattedMessage id="inspection.fail" />
                <CloseIcon sx={iconStyle} />
              </ToggleButton>
              <ToggleButton value={DeviceTestStatusEnum.NotTested}>
                <FormattedMessage id="inspection.notTested" />
              </ToggleButton>
            </StyledToggleButtonGroup>
          </Grid>
          {checklists && checklistStateSpec ? (
            <StyledContainer>
              <ChecklistRenderer
                checklist={checklistStateSpec}
                setChecklist={setChecklistStateSpec}
                onSave={save}
                renderSaveButton={
                  <PrimaryButton
                    fullWidth
                    onClick={save}
                    className="e2e-save-checklist-button"
                    data-testid="ChecklistDialogSaveButton"
                  >
                    <FormattedMessage id="general.save" />
                  </PrimaryButton>
                }
              />
            </StyledContainer>
          ) : (
            <PrimaryButton
              fullWidth
              onClick={onClose}
              className="e2e-save-status-button"
              data-testid="StatusDialogSaveButton"
              sx={{ marginLeft: '17px' }}
            >
              <FormattedMessage id="general.save" />
            </PrimaryButton>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};